import { useState, useEffect } from "react";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import Chip from "@mui/material/Chip";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Divider from "@mui/material/Divider";
import CircularProgress from "@mui/material/CircularProgress";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDInput from "components/MDInput";
import MDButton from "components/MDButton";
import MDSnackbar from "components/MDSnackbar";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import DataTable from "examples/Tables/DataTable";
import { useNavigate } from "react-router-dom";
import { useAuth } from "context/AuthContext";
import ExcelJS from "exceljs";
import { saveAs } from "file-saver";

const formatPakistanDateTime = (raw, includeSeconds = false) => {
    if (!raw) return "-";
    const str = String(raw).trim();
    if (!str || str.toLowerCase() === "n/a" || str === "-") return "-";
    const d = new Date(str);
    if (isNaN(d.getTime())) return str;
    const opts = {
        timeZone: "Asia/Karachi",
        month: "short",
        day: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        hour12: true,
    };
    if (includeSeconds) {
        opts.second = "2-digit";
    }
    return d.toLocaleString("en-US", opts);
};

// ─── Mobile Card ───────────────────────────────────────────────
const MobileCard = ({ r }) => {
    const securityCharges = r.charges ? r.charges.filter(c =>
        (c.description && c.description.toLowerCase().includes('security')) ||
        (c.title && c.title.toLowerCase().includes('security'))
    ) : [];

    const refunds = securityCharges.filter(c => {
        const amtStr = String(c.amount || "").trim();
        return amtStr !== "" && parseFloat(amtStr) < 0;
    });

    let displayCharge = {};
    if (refunds.length > 0) {
        displayCharge = refunds[0];
    } else if (securityCharges.length > 0) {
        displayCharge = securityCharges[0];
    }

    const amtStr = String(displayCharge.amount || "").trim();
    const isPaid = amtStr !== "" && parseFloat(amtStr) < 0;
    const requiredAmount = parseFloat(r.securityDeposit || 0);

    // Avatar initials
    const initials = (r.guestName || "?").split(" ").map(w => w[0]).join("").substring(0, 2).toUpperCase();

    const Row = ({ icon, label, value, valueColor }) => (
        <MDBox display="flex" justifyContent="space-between" alignItems="flex-start" mb={0.6}>
            <MDTypography variant="caption" sx={{ color: "#9E9E9E", minWidth: "140px", flexShrink: 0 }}>
                {icon} {label}
            </MDTypography>
            <MDTypography variant="caption" fontWeight="medium" sx={{ color: valueColor || "#212121", textAlign: "right" }}>
                {value || "-"}
            </MDTypography>
        </MDBox>
    );

    return (
        <MDBox mb={2} sx={{
            background: isPaid ? "#F9FFF9" : "#FFFDF5",
            borderRadius: "14px",
            boxShadow: isPaid
                ? "0 2px 12px rgba(76,175,80,0.12)"
                : "0 2px 12px rgba(255,152,0,0.12)",
            overflow: "hidden"
        }}>
            {/* ── Header ── */}
            <MDBox display="flex" justifyContent="space-between" alignItems="center" px={2} pt={2} pb={1}>
                <MDBox display="flex" alignItems="center" gap={1.5}>
                    {/* Avatar */}
                    <MDBox sx={{
                        width: 36, height: 36, borderRadius: "50%",
                        background: isPaid ? "#BBDEFB" : "#FFE0B2",
                        display: "flex", alignItems: "center", justifyContent: "center",
                        fontSize: "12px", fontWeight: "bold",
                        color: isPaid ? "#1565C0" : "#E65100", flexShrink: 0
                    }}>
                        {initials}
                    </MDBox>
                    <MDBox>
                        <MDTypography variant="body2" fontWeight="bold" sx={{ color: "#212121" }}>#{r.id}</MDTypography>
                        <MDTypography variant="caption" sx={{ color: "#757575" }}>{r.guestName}</MDTypography>
                    </MDBox>
                </MDBox>
                {/* Status Badge */}
                <MDBox component="span" sx={{
                    background: isPaid ? "#E8F5E9" : "#FFF3E0",
                    color: isPaid ? "#2E7D32" : "#E65100",
                    borderRadius: "20px", px: 1.5, py: 0.4,
                    fontSize: "11px", fontWeight: "bold",
                    boxShadow: isPaid ? "0 1px 4px rgba(76,175,80,0.2)" : "0 1px 4px rgba(255,152,0,0.2)"
                }}>
                    {isPaid ? "✅ PAID" : "⏳ PENDING"}
                </MDBox>
            </MDBox>

            <MDTypography variant="caption" sx={{ color: "#9E9E9E", px: 2, pb: 1, display: "block" }}>{r.apartment}</MDTypography>

            <Divider sx={{ mx: 2, my: 0.5, borderColor: "#F0F0F0" }} />

            {/* ── Reservation Details ── */}
            <MDBox px={2} pt={1} pb={1}>
                <MDBox sx={{
                    display: "inline-block", background: "#E3F2FD", borderRadius: "10px",
                    px: 1.2, py: 0.3, mb: 1
                }}>
                    <MDTypography variant="caption" fontWeight="bold" sx={{ color: "#1565C0", fontSize: "10px", letterSpacing: "0.6px", textTransform: "uppercase" }}>
                        📋 Reservation Details
                    </MDTypography>
                </MDBox>
                <Row icon="📅" label="Arrival Date" value={r.checkIn ? r.checkIn.split("T")[0] : "-"} />
                <Row icon="📅" label="Departure Date" value={r.checkOut ? r.checkOut.split("T")[0] : "-"} />
                <Row icon="💰" label="Total Price" value={r.totalPrice} />
                <Row icon="🕒" label="Actual Check-in Time" value={r.actualCheckInTime || "-"} />
                <Row icon="🕒" label="Actual Check-out Time" value={r.actualCheckOutTime || "-"} />
                <Row icon="🔐" label="Security Deposit" value={requiredAmount > 0 ? String(requiredAmount) : "-"} />
            </MDBox>

            <Divider sx={{ mx: 2, my: 0.5, borderColor: "#F0F0F0" }} />

            {/* ── Charge Details ── */}
            <MDBox px={2} pt={1} pb={1}>
                <MDBox sx={{
                    display: "inline-block", background: "#FFF8E1", borderRadius: "10px",
                    px: 1.2, py: 0.3, mb: 1
                }}>
                    <MDTypography variant="caption" fontWeight="bold" sx={{ color: "#E65100", fontSize: "10px", letterSpacing: "0.6px", textTransform: "uppercase" }}>
                        💳 Charge Details
                    </MDTypography>
                </MDBox>
                <Row icon="🔑" label="Charge ID" value={isPaid ? (displayCharge.id ? String(displayCharge.id) : "") : ""} />
                <Row icon="🏷️" label="Charge Title" value={isPaid ? (displayCharge.title || "") : ""} />
                <Row icon="📝" label="Description" value={isPaid ? (displayCharge.description || "") : ""} />
                <Row icon="💸" label="Amount" value={isPaid ? String(displayCharge.amount || "") : ""} valueColor="#E53935" />
                <Row icon="🕐" label="Charge Date & Time" value={isPaid ? formatPakistanDateTime(displayCharge.chargeDate) : ""} />
            </MDBox>


        </MDBox>
    );
};

// ─── Main Component ────────────────────────────────────────────
function SecurityDeposit() {
    const { user, isAuthenticated, loading: authLoading, isAdmin, isCustom, isViewOnly, hasPermission } = useAuth();
    const navigate = useNavigate();

    const nowTs = Date.now();
    const today = new Date(nowTs);

    const formatDate = (date) => {
        const y = date.getFullYear();
        const m = String(date.getMonth() + 1).padStart(2, "0");
        const d = String(date.getDate()).padStart(2, "0");
        return `${y}-${m}-${d}`;
    };

    const maxSelectableDate = formatDate(today);

    const [startDate, setStartDate] = useState(formatDate(today));
    const [endDate, setEndDate] = useState(formatDate(today));
    const [rawData, setRawData] = useState([]);
    const [searchQuery, setSearchQuery] = useState("");
    const [syncing, setSyncing] = useState(false);
    const [tableData, setTableData] = useState({ columns: [], rows: [] });
    const [loading, setLoading] = useState(false);
    const [filterStatus, setFilterStatus] = useState("all");
    const [snackbar, setSnackbar] = useState({ open: false, color: "info", title: "", message: "" });
    const [showDetailPopup, setShowDetailPopup] = useState(false);
    const [popupTableData, setPopupTableData] = useState({ columns: [], rows: [] });
    const [webhookLoading, setWebhookLoading] = useState(false);

    const closeSnackbar = () => setSnackbar(s => ({ ...s, open: false }));

    const fetchData = async (sd = startDate, ed = endDate) => {
        setLoading(true);
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 60000); // 60s timeout
        try {
            const query = new URLSearchParams({ startDate: sd, endDate: ed }).toString();
            const response = await fetch(`/api/security-deposit?${query}`, { signal: controller.signal });
            clearTimeout(timeoutId);
            const result = await response.json();
            if (result.success) setRawData(result.data);
            else setSnackbar({ open: true, color: "error", title: "Error", message: result.error || "Failed to load data" });
        } catch (error) {
            clearTimeout(timeoutId);
            if (error.name === "AbortError") {
                setSnackbar({ open: true, color: "warning", title: "Request Timed Out", message: "Data loading took too long. Please try again." });
            } else {
                console.error("Error fetching security deposit data:", error);
                setSnackbar({ open: true, color: "error", title: "Error", message: "Failed to fetch data. Check connection." });
            }
        } finally {
            setLoading(false);
        }
    };

    // Desktop table data
    useEffect(() => {
        const filteredData = rawData.filter(r => {
            const matchesSearch = String(r.id).includes(searchQuery);

            // Strictly require any charge or the reservation title to match 'security'
            const hasSecurityKeyword = (r.charges && r.charges.some(c =>
                (c.description && c.description.toLowerCase().includes('security')) ||
                (c.title && c.title.toLowerCase().includes('security'))
            )) || (r.title && r.title.toLowerCase().includes('security'));

            const shouldShow = hasSecurityKeyword && r.actualCheckInTime && r.actualCheckOutTime;

            // Enhanced Date Logic: Show if Departure Date OR Actual Check-out Time is in range
            const dateInRange = (dateStr) => {
                if (!dateStr) return false;
                if (startDate && dateStr < startDate) return false;
                if (endDate && dateStr > endDate) return false;
                return true;
            };

            const departureDate = r.checkOut ? r.checkOut.split("T")[0] : null;
            let actualOutDate = null;

            if (r.actualCheckOutTime) {
                // Handle "MM/DD/YYYY, ..."
                const slashParts = r.actualCheckOutTime.split(',')[0].trim().split('/');
                if (slashParts.length === 3) {
                    actualOutDate = `${slashParts[2]}-${slashParts[0].padStart(2, "0")}-${slashParts[1].padStart(2, "0")}`;
                } else {
                    // Handle "YYYY-MM-DD ..."
                    const dashParts = r.actualCheckOutTime.split(' ')[0].split('-');
                    if (dashParts.length === 3 && dashParts[0].length === 4) {
                        actualOutDate = r.actualCheckOutTime.split(' ')[0];
                    }
                }
            }

            const matchesDate = dateInRange(departureDate) || dateInRange(actualOutDate);
            return matchesSearch && matchesDate && shouldShow;
        });

        const columns = [
            { Header: "Reservation ID", accessor: "reservationId", width: "8%" },
            { Header: "Guest Name", accessor: "guestName", width: "10%" },
            { Header: "Listing Name", accessor: "listingName", width: "12%" },
            { Header: "Arrival Date", accessor: "arrivalDate", width: "8%" },
            { Header: "Departure Date", accessor: "departureDate", width: "8%" },
            { Header: "Total Price", accessor: "totalPrice", width: "8%" },
            { Header: "Actual Check-in Time", accessor: "actualCheckInTime", width: "10%" },
            { Header: "Actual Check-out Time", accessor: "actualCheckOutTime", width: "10%" },
            { Header: "Total", accessor: "total", width: "8%" },
            { Header: "Charge id", accessor: "chargeId", width: "8%" },
            { Header: "Charge Title", accessor: "chargeTitle", width: "10%" },
            { Header: "Description", accessor: "description", width: "10%" },
            { Header: "Amount", accessor: "amount", width: "8%" },
            { Header: "Charge Date and Time", accessor: "chargeDate", width: "10%" },
            { Header: "Status", accessor: "status", width: "8%" },
        ];

        const rows = [];
        filteredData.forEach(r => {
            const requiredAmount = parseFloat(r.securityDeposit || 0);

            // Only consider charges that explicitly mention security
            const securityCharges = r.charges ? r.charges.filter(c =>
                (c.description && c.description.toLowerCase().includes('security')) ||
                (c.title && c.title.toLowerCase().includes('security'))
            ) : [];

            const refunds = securityCharges.filter(c => {
                const amtStr = String(c.amount || "").trim();
                return amtStr !== "" && parseFloat(amtStr) < 0;
            });

            // Render every single refund found as its own row
            let chargesToRender = [{}];
            if (refunds.length > 0) {
                chargesToRender = refunds;
            } else if (securityCharges.length > 0) {
                chargesToRender = [securityCharges[0]];
            }
            chargesToRender.forEach(c => {
                const amtStr = String(c.amount || "").trim();
                const isPaid = amtStr !== "" && parseFloat(amtStr) < 0;

                rows.push({
                    reservationId: <MDTypography variant="caption" color="text" fontWeight="medium">{r.id}</MDTypography>,
                    guestName: <MDTypography variant="caption" color="text" fontWeight="medium">{r.guestName}</MDTypography>,
                    listingName: <MDTypography variant="caption" color="text" fontWeight="medium">{r.apartment}</MDTypography>,
                    arrivalDate: <MDTypography variant="caption" color="text">{r.checkIn ? r.checkIn.split("T")[0] : ""}</MDTypography>,
                    departureDate: <MDTypography variant="caption" color="text">{r.checkOut ? r.checkOut.split("T")[0] : ""}</MDTypography>,
                    totalPrice: <MDTypography variant="caption" color="text" fontWeight="medium">{r.totalPrice}</MDTypography>,
                    actualCheckInTime: <MDTypography variant="caption" color="text">{r.actualCheckInTime || "-"}</MDTypography>,
                    actualCheckOutTime: <MDTypography variant="caption" color="text">{r.actualCheckOutTime || "-"}</MDTypography>,
                    total: <MDTypography variant="caption" color="info" fontWeight="bold">{requiredAmount}</MDTypography>,
                    chargeId: <MDTypography variant="caption" color="text">{isPaid ? (c.id || "") : ""}</MDTypography>,
                    chargeTitle: <MDTypography variant="caption" color="text">{isPaid ? (c.title || "") : ""}</MDTypography>,
                    description: <MDTypography variant="caption" color="text">{isPaid ? (c.description || "") : ""}</MDTypography>,
                    amount: <MDTypography variant="caption" color="text" fontWeight="bold">{isPaid ? (c.amount || "") : ""}</MDTypography>,
                    chargeDate: <MDTypography variant="caption" color="text">{isPaid ? (c.chargeDate || "") : ""}</MDTypography>,
                    status: (
                        <MDBox ml={-1}>
                            <MDTypography variant="caption" fontWeight="bold" color={isPaid ? "success" : "warning"}>
                                {isPaid ? "PAID" : "PENDING"}
                            </MDTypography>
                        </MDBox>
                    )
                });
            });
        });


        setTableData({ columns, rows });
    }, [rawData, searchQuery, startDate, endDate]);

    // Mobile filtered data with status chip filter
    const mobileFilteredData = rawData.filter(r => {
        const matchesSearch = !searchQuery || String(r.id).includes(searchQuery);

        // Strictly require any charge or the reservation title to match 'security'
        const hasSecurityKeyword = (r.charges && r.charges.some(c =>
            (c.description && c.description.toLowerCase().includes('security')) ||
            (c.title && c.title.toLowerCase().includes('security'))
        )) || (r.title && r.title.toLowerCase().includes('security'));

        const shouldShow = hasSecurityKeyword && r.actualCheckInTime && r.actualCheckOutTime;

        // Enhanced Date Logic for Mobile
        const dateInRange = (dateStr) => {
            if (!dateStr) return false;
            if (startDate && dateStr < startDate) return false;
            if (endDate && dateStr > endDate) return false;
            return true;
        };

        const departureDate = r.checkOut ? r.checkOut.split("T")[0] : null;
        let actualOutDate = null;
        if (r.actualCheckOutTime) {
            const slashParts = r.actualCheckOutTime.split(',')[0].trim().split('/');
            if (slashParts.length === 3) {
                actualOutDate = `${slashParts[2]}-${slashParts[0].padStart(2, "0")}-${slashParts[1].padStart(2, "0")}`;
            } else {
                const dashParts = r.actualCheckOutTime.split(' ')[0].split('-');
                if (dashParts.length === 3 && dashParts[0].length === 4) {
                    actualOutDate = r.actualCheckOutTime.split(' ')[0];
                }
            }
        }
        const matchesDate = dateInRange(departureDate) || dateInRange(actualOutDate);

        // A deposit is PAID only if there is a negative, empty, or 0 amount security charge
        const isPaid = r.charges && r.charges.some(c => {
            const amtStr = String(c.amount || "").trim();
            const isNegative = amtStr !== "" && parseFloat(amtStr) < 0;
            return isNegative &&
                ((c.description && c.description.toLowerCase().includes('security')) ||
                    (c.title && c.title.toLowerCase().includes('security')));
        });

        let matchesStatus = true;
        if (filterStatus === "paid") matchesStatus = isPaid;
        else if (filterStatus === "pending") matchesStatus = !isPaid;

        return matchesSearch && matchesDate && shouldShow && matchesStatus;
    });

    const handleStartDateChange = (e) => setStartDate(e.target.value);
    const handleEndDateChange = (e) => setEndDate(e.target.value);

    // Fetch data only when "Refresh" is manually clicked
    const handleRefresh = () => {
        if (!startDate || !endDate) {
            setSnackbar({ open: true, color: "warning", title: "Warning", message: "Please select both a From Date and a To Date before refreshing." });
            return;
        }
        fetchData(startDate, endDate);
    };

    const handleDownloadExcel = async () => {
        if (!rawData || rawData.length === 0) {
            setSnackbar({ open: true, color: "warning", title: "Warning", message: "No data available to download." });
            return;
        }

        const filteredToDownload = rawData.filter(r => {
            const matchesSearch = String(r.id).includes(searchQuery);
            const hasSecurityKeyword = (r.charges && r.charges.some(c =>
                (c.description && c.description.toLowerCase().includes('security')) ||
                (c.title && c.title.toLowerCase().includes('security'))
            )) || (r.title && r.title.toLowerCase().includes('security'));
            const shouldShow = hasSecurityKeyword && r.actualCheckInTime && r.actualCheckOutTime;
            const dateInRange = (dateStr) => {
                if (!dateStr) return false;
                if (startDate && dateStr < startDate) return false;
                if (endDate && dateStr > endDate) return false;
                return true;
            };
            const departureDate = r.checkOut ? r.checkOut.split("T")[0] : null;
            let actualOutDate = null;
            if (r.actualCheckOutTime) {
                const slashParts = r.actualCheckOutTime.split(',')[0].trim().split('/');
                if (slashParts.length === 3) {
                    actualOutDate = `${slashParts[2]}-${slashParts[0].padStart(2, "0")}-${slashParts[1].padStart(2, "0")}`;
                } else {
                    const dashParts = r.actualCheckOutTime.split(' ')[0].split('-');
                    if (dashParts.length === 3 && dashParts[0].length === 4) {
                        actualOutDate = r.actualCheckOutTime.split(' ')[0];
                    }
                }
            }
            const matchesDate = dateInRange(departureDate) || dateInRange(actualOutDate);
            return matchesSearch && matchesDate && shouldShow;
        });

        if (filteredToDownload.length === 0) {
            setSnackbar({ open: true, color: "info", title: "Notice", message: "No records to download for the selected dates." });
            return;
        }

        const wb = new ExcelJS.Workbook();
        const ws = wb.addWorksheet("Security Deposit");

        const headers = [
            "Reservation ID", "Guest Name", "Listing Name",
            "Arrival Date", "Departure Date", "Total Price", "Actual Check-in Time",
            "Actual Check-out Time", "Total", "Charge ID", "Charge Title",
            "Description", "Amount", "Charge Date and Time", "Status"
        ];
        ws.addRow(headers);
        ws.getRow(1).font = { bold: true };

        filteredToDownload.forEach(r => {
            const requiredAmount = parseFloat(r.securityDeposit || 0);

            const securityCharges = r.charges ? r.charges.filter(c =>
                (c.description && c.description.toLowerCase().includes('security')) ||
                (c.title && c.title.toLowerCase().includes('security'))
            ) : [];

            const refunds = securityCharges.filter(c => {
                const amtStr = String(c.amount || "").trim();
                return amtStr !== "" && parseFloat(amtStr) < 0;
            });

            let chargesToRender = [{}];
            if (refunds.length > 0) {
                chargesToRender = refunds;
            } else if (securityCharges.length > 0) {
                chargesToRender = [securityCharges[0]];
            }

            chargesToRender.forEach(c => {
                const amtStr = String(c.amount || "").trim();
                const isPaid = amtStr !== "" && parseFloat(amtStr) < 0;

                ws.addRow([
                    r.id || "",
                    r.guestName || "",
                    r.apartment || "",
                    r.checkIn ? r.checkIn.split("T")[0] : "",
                    r.checkOut ? r.checkOut.split("T")[0] : "",
                    r.totalPrice || "",
                    r.actualCheckInTime || "-",
                    r.actualCheckOutTime || "-",
                    requiredAmount,
                    isPaid ? (c.id || "") : "",
                    isPaid ? (c.title || "") : "",
                    isPaid ? (c.description || "") : "",
                    isPaid ? (c.amount || "") : "",
                    isPaid ? (c.chargeDate || "") : "",
                    isPaid ? "PAID" : "PENDING"
                ]);
            });
        });

        // Auto width columns
        ws.columns.forEach((column) => {
            let maxLength = 0;
            column.eachCell({ includeEmpty: true }, (cell) => {
                const len = cell.value ? cell.value.toString().trim().length : 10;
                if (len > maxLength) maxLength = len;
            });
            column.width = Math.min(Math.max(maxLength + 2, 10), 30);
        });

        const buffer = await wb.xlsx.writeBuffer();
        const dateStr = new Date().toLocaleDateString("en-GB").replace(/\//g, "-");
        saveAs(new Blob([buffer]), `Security_Deposit_${dateStr}.xlsx`);
    };

    const handleHostawayLookup = async () => {
        if (!searchQuery) { alert("Please enter a Reservation ID"); return; }
        setWebhookLoading(true);
        try {
            const response = await fetch(`/api/security-deposit-search/manual-check?reservationId=${searchQuery}`);
            const result = await response.json();
            if (result.success && result.negativeAmountFound) {
                let records = result.paymentData || [];
                const negativeRecords = records.filter(r => {
                    const amtStr = String(r.fields["Amount"] || "").trim();
                    return amtStr !== "" && parseFloat(amtStr) < 0;
                });

                // If there are refunds found, ONLY show those refunds
                if (negativeRecords.length > 0) {
                    records = negativeRecords;
                }
                const rows = records.map(r => ({
                    reservationId: <MDTypography variant="caption" color="text">{r.fields["reservationId"] || result.reservationId}</MDTypography>,
                    listingName: <MDTypography variant="caption" color="text">{r.fields["Listing Name"] || "-"}</MDTypography>,
                    guestName: <MDTypography variant="caption" color="text">{r.fields["Guest Name"] || "-"}</MDTypography>,
                    status: <MDTypography variant="caption" color="text">{r.fields["Status"] || "-"}</MDTypography>,
                    checkIn: <MDTypography variant="caption" color="text">{r.fields["Check In"] ? r.fields["Check In"].split("T")[0] : "-"}</MDTypography>,
                    checkOut: <MDTypography variant="caption" color="text">{r.fields["Check Out"] ? r.fields["Check Out"].split("T")[0] : "-"}</MDTypography>,
                    amount: <MDTypography variant="caption" color="error" fontWeight="bold">{r.fields["Amount"]}</MDTypography>,
                    chargeDate: <MDTypography variant="caption" color="text">{formatPakistanDateTime(r.fields["Charge Date"])}</MDTypography>,
                    title: <MDTypography variant="caption" color="text">{r.fields["Title"]}</MDTypography>,
                    description: <MDTypography variant="caption" color="text">{r.fields["Description"]}</MDTypography>,
                    type: <MDTypography variant="caption" color="text">{r.fields["Type"]}</MDTypography>,
                    totalPrice: <MDTypography variant="caption" color="text">{r.fields["Total Price"] || "-"}</MDTypography>,
                    paymentStatus: <MDTypography variant="caption" color="success" fontWeight="bold">{r.fields["Payment Status"] || "-"}</MDTypography>
                }));
                const columns = [
                    { Header: "Res ID", accessor: "reservationId", width: "10%" },
                    { Header: "Guest", accessor: "guestName", width: "10%" },
                    { Header: "Listing", accessor: "listingName", width: "10%" },
                    { Header: "Status", accessor: "status", width: "8%" },
                    { Header: "Check In", accessor: "checkIn", width: "8%" },
                    { Header: "Check Out", accessor: "checkOut", width: "8%" },
                    { Header: "Amount", accessor: "amount", width: "8%" },
                    { Header: "Total Price", accessor: "totalPrice", width: "8%" },
                    { Header: "Charge Date", accessor: "chargeDate", width: "10%" },
                    { Header: "Title", accessor: "title", width: "8%" },
                    { Header: "Desc", accessor: "description", width: "12%" },
                    { Header: "Type", accessor: "type", width: "8%" },
                    { Header: "Pay Status", accessor: "paymentStatus", width: "10%" },
                ];
                setPopupTableData({ columns, rows });
                setShowDetailPopup(true);
            } else {
                setSnackbar({ open: true, color: "info", title: "Notice", message: "No security deposit found for this reservation." });
            }
        } catch (error) {
            setSnackbar({ open: true, color: "error", title: "Error", message: error.message || "Unknown error" });
        } finally {
            setWebhookLoading(false);
        }
    };

    const handleSync = async () => {
        setSyncing(true);
        try {
            await fetch("/api/security-deposit/sync");
            await fetchData();
            setSnackbar({ open: true, color: "success", title: "Sync", message: "Sync Completed Successfully!" });
        } catch (error) {
            setSnackbar({ open: true, color: "error", title: "Sync Error", message: "Sync process failed." });
        } finally {
            setSyncing(false);
        }
    };

    useEffect(() => {
        if (!authLoading && startDate && endDate) fetchData();
    }, [authLoading]);

    if (!authLoading && (!isAuthenticated || !user)) {
        window.location.href = "/authentication/sign-in";
        return null;
    }

    const canView = isAdmin() || isViewOnly() || (isCustom() && (hasPermission("securityDeposit", "view") || hasPermission("securityDeposit", "complete")));
    if (!authLoading && !canView) { navigate("/dashboard"); return null; }

    if (authLoading || loading) {
        return (
            <DashboardLayout>
                <DashboardNavbar />
                <MDBox display="flex" flexDirection="column" justifyContent="center" alignItems="center" minHeight="60vh">
                    <CircularProgress color="info" size={60} />
                    <MDTypography variant="h6" mt={2} color="secondary">Loading Please wait</MDTypography>
                </MDBox>
                <Footer />
            </DashboardLayout>
        );
    }

    return (
        <DashboardLayout>
            <DashboardNavbar />
            <MDBox pt={1} pb={3}>
                <Card>
                            {/* ══════════ DESKTOP HEADER (md+) ══════════ */}
                            <MDBox display={{ xs: "none", md: "flex" }} p={2} justifyContent="space-between" alignItems="center" bgColor="grey-100" borderRadius="lg">
                                <MDTypography variant="h6" color="dark">
                                    Security Deposit
                                    <MDTypography component="span" variant="button" color="text" fontWeight="bold" ml={2}>
                                        (Total Reservations: {tableData.rows.length})
                                    </MDTypography>
                                </MDTypography>
                                <MDBox display="flex" alignItems="center" gap={2}>
                                    <MDInput label="Search..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} size="small" sx={{ width: 200 }} />
                                    {searchQuery && (
                                        <MDButton variant="gradient" color="success" onClick={handleSync} disabled={syncing} size="small">
                                            {syncing ? "Syncing..." : "Start"}
                                        </MDButton>
                                    )}
                                    <MDBox display="flex" alignItems="center" gap={1}>
                                        <MDTypography variant="caption" fontWeight="bold">From:</MDTypography>
                                        <MDInput type="date" value={startDate} onChange={handleStartDateChange} onKeyDown={e => e.preventDefault()} inputProps={{ max: maxSelectableDate }} size="small" />
                                    </MDBox>
                                    <MDBox display="flex" alignItems="center" gap={1}>
                                        <MDTypography variant="caption" fontWeight="bold">To:</MDTypography>
                                        <MDInput type="date" value={endDate} onChange={handleEndDateChange} onKeyDown={e => e.preventDefault()} inputProps={{ max: maxSelectableDate }} size="small" />
                                    </MDBox>
                                    <MDButton variant="gradient" color="info" onClick={handleRefresh} size="small" sx={{ ml: 1 }}>
                                        Refresh
                                    </MDButton>
                                    <MDButton variant="gradient" color="success" onClick={handleDownloadExcel} size="small" sx={{ ml: 1 }}>
                                        Download Excel
                                    </MDButton>
                                </MDBox>
                            </MDBox>

                            {/* ══════════ MOBILE HEADER (xs/sm) ══════════ */}
                            <MDBox display={{ xs: "block", md: "none" }} p={2} sx={{ background: "#f8f9fa", borderBottom: "1px solid #e0e0e0" }}>
                                <MDTypography variant="h6" color="dark" mb={2} fontWeight="bold">
                                    Security Deposit
                                    <MDTypography component="span" variant="caption" color="text" ml={1}>
                                        ({mobileFilteredData.length} records)
                                    </MDTypography>
                                </MDTypography>


                                <MDBox display="flex" gap={1} mb={1.5} alignItems="center">
                                    <MDInput type="date" value={startDate} onChange={handleStartDateChange} onKeyDown={e => e.preventDefault()} inputProps={{ max: maxSelectableDate }} size="small" sx={{ flex: 1 }} />
                                    <MDInput type="date" value={endDate} onChange={handleEndDateChange} onKeyDown={e => e.preventDefault()} inputProps={{ max: maxSelectableDate }} size="small" sx={{ flex: 1 }} />
                                    <MDButton variant="gradient" color="info" onClick={handleRefresh} size="small" sx={{ px: 2, minWidth: "auto" }}>
                                        ↻
                                    </MDButton>
                                    <MDButton variant="gradient" color="success" onClick={handleDownloadExcel} size="small" sx={{ px: 2, minWidth: "auto" }}>
                                        ⬇
                                    </MDButton>
                                </MDBox>

                                <MDBox display="flex" gap={1}>
                                    {["all", "paid", "pending"].map(f => (
                                        <Chip key={f}
                                            label={f === "all" ? "All" : f === "paid" ? "✅ Paid" : "⏳ Pending"}
                                            onClick={() => setFilterStatus(f)}
                                            sx={{
                                                flex: 1,
                                                backgroundColor: filterStatus === f ? "#1976d2" : "#e9ecef",
                                                color: filterStatus === f ? "#fff" : "#495057",
                                                fontWeight: "bold",
                                                borderRadius: "20px",
                                                fontSize: "12px",
                                                cursor: "pointer",
                                                "&:hover": { backgroundColor: filterStatus === f ? "#1565c0" : "#dee2e6" }
                                            }}
                                        />
                                    ))}
                                </MDBox>
                            </MDBox>

                            {/* ══════════ MOBILE CARDS (xs/sm) ══════════ */}
                            <MDBox display={{ xs: "block", md: "none" }} p={2} sx={{ background: "#f5f5f5", minHeight: "50vh" }}>
                                {mobileFilteredData.length === 0 ? (
                                    <MDBox textAlign="center" py={4}>
                                        <MDTypography variant="body2" color="secondary">No records found</MDTypography>
                                    </MDBox>
                                ) : (
                                    mobileFilteredData.map((r, i) => <MobileCard key={i} r={r} />)
                                )}
                            </MDBox>

                            {/* ══════════ DESKTOP TABLE (md+) ══════════ */}
                            <MDBox display={{ xs: "none", md: "block" }} pt={3}>
                                <DataTable table={tableData} isSorted={false} entriesPerPage={{ defaultValue: 500, show: false }} showTotalEntries={false} noEndBorder />
                            </MDBox>
                        </Card>
            </MDBox>
            <Footer />

            <Dialog open={showDetailPopup} onClose={() => setShowDetailPopup(false)} maxWidth="xl" fullWidth>
                <DialogTitle>Reservation Details</DialogTitle>
                <DialogContent>
                    <MDBox pt={1}>
                        <DataTable table={popupTableData} isSorted={false} entriesPerPage={false} showTotalEntries={false} noEndBorder />
                    </MDBox>
                </DialogContent>
                <DialogActions>
                    <MDButton onClick={() => setShowDetailPopup(false)} color="dark">Close</MDButton>
                </DialogActions>
            </Dialog>

            <MDSnackbar color={snackbar.color} icon="notifications" title={snackbar.title} content={snackbar.message} dateTime="Just now" open={snackbar.open} close={closeSnackbar} anchorOrigin={{ vertical: "top", horizontal: "center" }} />
        </DashboardLayout>
    );
}

export default SecurityDeposit;
